export const feedHasTracks = (f) => f?.stream && f.stream.getTracks?.().length > 0;
export const feedHasVideo = (f) =>
  feedHasTracks(f) && f.stream.getVideoTracks?.()?.length > 0;
export const feedHasAudio = (f) =>
  feedHasTracks(f) && f.stream.getAudioTracks?.()?.length > 0;

const USERMEDIA_PURPOSE = 'm.usermedia';

const getFeedTrackSignature = (feed) => {
  const stream = feed?.stream;
  const tracks = stream?.getTracks?.() || [];
  const trackSig = tracks
    .map((track) => `${track.kind}:${track.id}:${track.readyState}:${track.muted}:${track.enabled}`)
    .join('|');
  const videoMuted = typeof feed?.isVideoMuted === 'function' ? String(feed.isVideoMuted()) : '';
  const audioMuted = typeof feed?.isAudioMuted === 'function' ? String(feed.isAudioMuted()) : '';
  return `${trackSig}::v${videoMuted}:a${audioMuted}`;
};

export const getRemoteUsermediaFeed = (remoteFeeds) => {
  if (!Array.isArray(remoteFeeds) || !remoteFeeds.length) return null;
  return (
    remoteFeeds.find((feed) => feed?.purpose === USERMEDIA_PURPOSE) ??
    remoteFeeds.find((feed) => typeof feed?.isVideoMuted === 'function') ??
    null
  );
};

/** True when the remote party muted or removed their camera (black frames, not “no stream yet”). */
export const isCallFeedVideoMuted = (feed) => {
  if (!feed) return false;
  if (typeof feed.isVideoMuted === 'function') return Boolean(feed.isVideoMuted());
  const tracks = feed.stream?.getVideoTracks?.() ?? [];
  if (!tracks.length) return true;
  // Do not treat WebRTC `track.muted` as camera-off — iOS starts muted until the first frame.
  return tracks.every((track) => track.readyState !== 'live' || track.enabled === false);
};

/**
 * Compare Matrix feed arrays by stable stream/track identity so we avoid
 * rerendering the call UI on noisy duplicate feed events.
 */
export const areFeedsEquivalent = (nextFeeds, prevFeeds) => {
  if (nextFeeds === prevFeeds) return true;
  if (!Array.isArray(nextFeeds) || !Array.isArray(prevFeeds)) return false;
  if (nextFeeds.length !== prevFeeds.length) return false;

  for (let i = 0; i < nextFeeds.length; i += 1) {
    const next = nextFeeds[i];
    const prev = prevFeeds[i];
    const nextStream = next?.stream || null;
    const prevStream = prev?.stream || null;
    if (nextStream !== prevStream) return false;
    if (getFeedTrackSignature(next) !== getFeedTrackSignature(prev)) return false;
  }
  return true;
};

const getKindTracks = (stream, kind) =>
  (stream?.getTracks?.() || []).filter((track) => track?.kind === kind);

const streamHasTrackKind = (stream, kind) => getKindTracks(stream, kind).length > 0;

const streamHasLiveTrackKind = (stream, kind) =>
  getKindTracks(stream, kind).some((track) => track?.readyState === 'live');

/**
 * Prefer SDK usermedia when it contains the requested kind, otherwise fall back
 * to feed stream(s) so call UIs remain stable across feed churn.
 */
export const pickPreferredRemoteStream = ({
  sdkRemoteStream,
  feedStream,
  kind,
}) => {
  // Prefer sources that currently expose a live track for the requested kind.
  // SDK streams can keep stale (ended) tracks during reconnect/device churn.
  if (streamHasLiveTrackKind(sdkRemoteStream, kind)) return sdkRemoteStream;
  if (streamHasLiveTrackKind(feedStream, kind)) return feedStream;
  if (streamHasTrackKind(sdkRemoteStream, kind)) return sdkRemoteStream;
  return feedStream ?? sdkRemoteStream ?? null;
};

/**
 * Some reconnect/device churn paths can temporarily expose duplicate remote audio
 * tracks. Playing all of them together causes echo/repeat artifacts.
 * Keep playback pinned to one best active track.
 */
/** True when the stream has at least one live camera track (not audio-only). */
export const streamHasLiveVideoTrack = (stream) =>
  (stream?.getVideoTracks?.() ?? []).some((track) => track?.readyState === 'live');

/**
 * Bind only non-ended video tracks to <video> elements (avoids black frame when the
 * wrapper stream is audio-only or carries ended video tracks after reconnect).
 */
export const getVideoPlaybackStream = (stream) => {
  if (!stream) return null;
  const liveVideoTracks = (stream.getVideoTracks?.() ?? []).filter(
    (track) => track.readyState === 'live'
  );
  if (!liveVideoTracks.length) return null;
  // iOS Safari often shows a solid/black preview when we wrap tracks that are
  // already owned by another stream. Keep the original whenever it has live video.
  return stream;
};

export const getSingleRemoteAudioTrackStream = (stream) => {
  if (!stream) return null;
  const audioTracks = stream.getAudioTracks?.() || [];
  if (!audioTracks.length) return null;
  if (audioTracks.length === 1) {
    return audioTracks[0]?.readyState === 'live' ? stream : null;
  }

  const preferredTrack =
    audioTracks.find((track) => track.readyState === 'live' && track.enabled) ??
    audioTracks.find((track) => track.readyState === 'live') ??
    audioTracks.find((track) => track.enabled) ??
    audioTracks[0];
  if (!preferredTrack) return null;

  return new MediaStream([preferredTrack]);
};
